import React, { useEffect, useState, useRef } from 'react';
import { getAnalyticsConsentForServer } from '@/services/consent';
import { authService, OAuthProvider } from '@/services/authService';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { analytics } from '@/services/analytics';

interface OAuthCallbackProps {
  provider: OAuthProvider;
  onSuccess?: () => void;
  onError?: (error: string) => void;
}

const OAuthCallback: React.FC<OAuthCallbackProps> = ({ provider, onSuccess, onError }) => {
  const [status, setStatus] = useState<'processing' | 'success' | 'error'>('processing');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [showLoading, setShowLoading] = useState(false);
  const { refreshUser } = useAuth();
  const { theme } = useTheme();
  const isDarkMode = theme === 'dark';
  const hasHandledCallback = useRef(false);

  // Use a ref to store the latest onSuccess callback to avoid stale closures
  const onSuccessRef = useRef(onSuccess);

  useEffect(() => {
    onSuccessRef.current = onSuccess;
  }, [onSuccess]);

  useEffect(() => {
    // Only show loading after 1 second to avoid loading message flashing
    const timer = setTimeout(() => {
      setShowLoading(true);
    }, 1000);

    if (!hasHandledCallback.current) {
      hasHandledCallback.current = true;
      handleCallback();
    }

    return () => clearTimeout(timer);
  }, []);

  const handleCallback = async () => {
    try {
      // Try to get tokens from hash fragment first (new flow with #)
      const hash = window.location.hash.substring(1); // Remove leading #
      let urlParams = new URLSearchParams(hash);

      // Check if hash has tokens
      let accessToken = urlParams.get('access_token');
      let refreshToken = urlParams.get('refresh_token');

      // Fallback to query params if hash doesn't have tokens (old flow with ?)
      if (!accessToken || !refreshToken) {
        urlParams = new URLSearchParams(window.location.search);
        accessToken = urlParams.get('access_token');
        refreshToken = urlParams.get('refresh_token');
      }

      const expiresIn = urlParams.get('expires_in');
      const error = urlParams.get('error');

      if (error) {
        throw new Error(`OAuth error: ${error}`);
      }

      let authResponse;

      if (accessToken && refreshToken) {
        // New flow: Backend already handled OAuth and redirected with tokens
        authResponse = {
          access_token: accessToken,
          refresh_token: refreshToken,
          expires_in: parseInt(expiresIn || '1800', 10),
        };

        // Store tokens (use the same keys as authService)
        localStorage.setItem('aleph_access_token', authResponse.access_token);
        localStorage.setItem('aleph_refresh_token', authResponse.refresh_token);
        const expiryTime = Date.now() + (authResponse.expires_in * 1000);
        localStorage.setItem('aleph_token_expiry', expiryTime.toString());
      } else {
        // Fallback: Check if we have a code
        const code = urlParams.get('code');

        if (code) {
          // We have a code but no tokens - call backend API to exchange the code
          // and get tokens directly (since redirect approach doesn't work with SPA)
          const backendUrl = (import.meta.env.VITE_API_BASE_URL || '').replace(/\/$/, '');

          const response = await fetch(`${backendUrl}/api/auth/token/${provider}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              code,
              state: urlParams.get('state'),
              redirect_uri: `${window.location.origin}${window.location.pathname}`,
              // This is where the web OAuth signup creates the user, so the
              // consent decision has to arrive with it — otherwise a visitor
              // who declined and then signed in with Google would be tracked
              // server-side on the strength of the account alone.
              analytics_consent: getAnalyticsConsentForServer(),
            }),
          });

          if (!response.ok) {
            const errorData = await response.json().catch(() => ({ detail: 'Token exchange failed' }));
            throw new Error(errorData.detail || 'Token exchange failed');
          }

          authResponse = await response.json();

          // Store tokens
          localStorage.setItem('aleph_access_token', authResponse.access_token);
          localStorage.setItem('aleph_refresh_token', authResponse.refresh_token);
          const expiryTime = Date.now() + (authResponse.expires_in * 1000);
          localStorage.setItem('aleph_token_expiry', expiryTime.toString());

          // Store user data from API response
          if (authResponse.user) {
            localStorage.setItem('aleph_user', JSON.stringify(authResponse.user));
          }
        } else {
          throw new Error('No authorization code or tokens received');
        }
      }

      // For new flow with hash tokens, we need to fetch and store user data
      if (accessToken && refreshToken) {
        const user = await authService.getCurrentUser();
        if (user) {
          localStorage.setItem('aleph_user', JSON.stringify(user));
        } else {
          console.error('OAuth Callback - Failed to fetch user after storing tokens');
          throw new Error('Failed to fetch user data');
        }
      }

      // Schedule automatic token refresh
      authService.scheduleTokenRefresh(authResponse.expires_in);

      await refreshUser();
      authService.setLastLoginMethod(provider);

      analytics.track('Page View', { view: `oauth-callback-${provider}`, provider });

      setStatus('success');

      // Check if there's a saved redirect path
      const savedPath = sessionStorage.getItem('redirectAfterLogin');
      if (savedPath) {
        sessionStorage.removeItem('redirectAfterLogin');
        window.location.href = savedPath;
      } else {
        onSuccessRef.current?.();
      }

    } catch (error) {
      console.error('OAuth callback error:', error);
      const message = error instanceof Error ? error.message : 'Authentication failed';
      setErrorMessage(message);
      setStatus('error');
      onError?.(message);
    }
  };

  const themeClasses = isDarkMode ? 'text-white' : 'bg-white text-gray-900';

  return (
    <div className={`min-h-screen flex items-center justify-center ${themeClasses} transition-colors duration-300`} style={{ backgroundColor: isDarkMode ? 'bg-theme-dark' : 'bg-white' }}>
      <div className="text-center max-w-md mx-auto p-8">
        {status === 'processing' && showLoading && (
          <>
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <h2 className="text-2xl font-semibold mb-2">Completing Sign In</h2>
            <p className={`${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              Please wait while we complete your {provider} authentication...
            </p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
              <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-2xl font-semibold text-green-600 mb-2">Success!</h2>
            <p className={`${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              You have been successfully signed in. Redirecting...
            </p>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
              <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <h2 className="text-2xl font-semibold text-red-600 mb-2">Authentication Failed</h2>
            <p className={`mb-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              {errorMessage}
            </p>
            <button
              onClick={() => window.location.href = '/'}
              className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-full transition-colors cursor-pointer"
            >
              Try Again
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default OAuthCallback;
